import type { ReactNode } from 'react';
import { Chip } from '@jotpsych/ui';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Box, Stack, Typography } from '@mui/material';

import { sourceLabel } from '../labels';
import { formatEventDate } from '../vm/formatters';
import type { TimelineEventVM } from '../vm/types';

interface EventVisual {
  node: ReactNode;
  color: string;
}

const eventVisual = (kind: TimelineEventVM['kind']): EventVisual => {
  switch (kind) {
    case 'started':
      return { node: <PlayArrowIcon fontSize="inherit" />, color: 'success.main' };
    case 'restarted':
      return { node: <PlayCircleOutlinedIcon fontSize="inherit" />, color: 'success.main' };
    case 'increased':
      return { node: <ArrowUpwardIcon fontSize="inherit" />, color: 'primary.main' };
    case 'decreased':
    case 'taper':
      return { node: <ArrowDownwardIcon fontSize="inherit" />, color: 'warning.main' };
    case 'use_changed':
      return { node: <EditIcon fontSize="inherit" />, color: 'warning.main' };
    case 'held':
      return { node: <PauseCircleOutlinedIcon fontSize="inherit" />, color: 'warning.main' };
    case 'fill_recorded':
    case 'administration':
      return { node: <LocalPharmacyIcon fontSize="inherit" />, color: 'info.main' };
    case 'sent':
      return { node: <SendIcon fontSize="inherit" />, color: 'info.main' };
    case 'renewed':
      return { node: <AutorenewIcon fontSize="inherit" />, color: 'info.main' };
    case 'stopped':
    case 'cancelled':
      return { node: <StopCircleIcon fontSize="inherit" />, color: 'error.main' };
    default:
      return { node: <FiberManualRecordIcon fontSize="inherit" />, color: 'text.disabled' };
  }
};

interface PastPrescriptionRowProps {
  event: TimelineEventVM;
}

/**
 * One compact history line inside a medication's "past prescriptions" drawer:
 * kind icon, what changed, who/when, and the reporting source.
 */
export const PastPrescriptionRow: React.FC<PastPrescriptionRowProps> = ({ event }) => {
  const visual = eventVisual(event.kind);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          color: visual.color,
          fontSize: 18,
          lineHeight: 1,
          mt: '2px',
          display: 'inline-flex',
        }}
      >
        {visual.node}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {event.label}
        </Typography>
        {event.detail && (
          <Typography variant="caption" color="text.secondary">
            {event.detail}
          </Typography>
        )}
        {event.prescriber && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {event.prescriber}
          </Typography>
        )}
      </Box>

      <Stack spacing={0.5} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {formatEventDate(event)}
        </Typography>
        <Chip variant="soft" size="xSmall" color="default" label={sourceLabel[event.source]} />
      </Stack>
    </Box>
  );
};
